/**
 * API del módulo Reportería (builder de usuario · S11). Todo va contra los
 * endpoints ya existentes (§9): el cliente SOLO maneja TOKENS opacos y Rids
 * (nunca nombres reales de tabla/columna · RN-01). El interceptor de axios
 * antepone `/api/v1`.
 */
import { axiosClient } from "@/shared/api/axios-client";
import { encolarDescarga } from "@/shared/api/descarga-secuencial";

// ---- Catálogo (whitelist del usuario) ----

export interface CatalogoColumna {
  token: string;
  etiqueta: string;
  tipoDato: string | null;
  /** RN-20: luce como id/FK · se oculta del picker salvo override del admin. */
  ocultaEnPicker: boolean;
  /** RN-19: dato sensible · llega enmascarado y no ofrece selector de valores. */
  enmascarada: boolean;
}

export interface CatalogoTabla {
  token: string;
  etiqueta: string;
  columnas: CatalogoColumna[];
}

export interface Catalogo {
  tablas: CatalogoTabla[];
  columnasCalculadas: string[];
}

export interface RelacionSugerida {
  rid: string;
  tipo: string;
  tablaIzqToken: string;
  tablaDerToken: string;
  descripcion: string | null;
}

// ---- Definición del reporte (contrato cliente → servidor · §4.3) ----

export interface RepTablaSel {
  token: string;
  alias: string;
}

export interface RepColumnaSel {
  tablaAlias: string;
  token: string;
  etiqueta?: string | null;
  expresion?: string | null;
  agregacion?: string | null;
  /** Estado de UI (no lo interpreta el server) · para reabrir el diálogo de formato. */
  formato?: string | null;
  /** Estado de UI · mapa valor→texto de la transformación. */
  mapaValores?: string | null;
}

export interface RepJoin {
  tipo: string;
  tablaIzqAlias: string;
  tablaDerAlias: string;
  rid?: string | null;
  tokenIzq?: string | null;
  tokenDer?: string | null;
}

export interface RepFiltro {
  tablaAlias: string;
  token: string;
  operador: string;
  valor?: string | null;
  valor2?: string | null;
}

export interface RepColumnaCalc {
  nombre: string;
  formula: string;
  tipo: string; // "fila" | "agregado"
  formato?: string | null;
}

export interface RepOrden {
  tablaAlias: string;
  token: string;
  direccion: string; // "asc" | "desc"
}

export interface RepDefinicion {
  tablas: RepTablaSel[];
  columnas: RepColumnaSel[];
  joins?: RepJoin[] | null;
  filtros?: RepFiltro[] | null;
  filtroFormula?: string | null;
  columnasCalculadas?: RepColumnaCalc[] | null;
  orden?: RepOrden[] | null;
  limite?: number | null;
  agrupar?: boolean;
}

// ---- Resultado de preview ----

export interface ColumnaResultado {
  clave: string;
  etiqueta: string;
}

export interface RepResultadoPagina {
  columnas: ColumnaResultado[];
  filas: Array<Record<string, unknown>>;
  pagina: number;
  tamano: number;
  /** -1 cuando el conteo va diferido (incluirTotal=false). */
  totalFilas: number;
  paginas: number;
  totales?: Record<string, unknown> | null;
}

export interface PreviewParams {
  def: RepDefinicion;
  pagina: number;
  tamano: number;
  incluirTotal: boolean;
  muestra: boolean;
}

// ---- Persistencia ----

export interface TemplateDto {
  id: number;
  nombre: string;
  descripcion: string | null;
  fechaCreacion: string;
  fechaModificacion: string | null;
}

export interface ColumnaCalcDto {
  id: number;
  nombre: string;
  formula: string;
  tipo: string;
  formato: string | null;
}

export interface ReporteDefaultDto {
  id: number;
  idConfiguracion: number;
  nombre: string;
  descripcion: string | null;
  fechaCreacion: string;
  fechaModificacion: string | null;
}

// ---- Trabajos de exportación ----

export interface TrabajoDto {
  id: number;
  titulo: string;
  estado: string; // Pendiente | Procesando | Completado | Error | Expirado
  totalFilas: number | null;
  nombreArchivo: string | null;
  mensajeError: string | null;
  fechaCreacion: string;
  fechaFin: string | null;
  fechaExpiracion: string | null;
  descargable: boolean;
}

export interface PaginaTrabajos {
  items: TrabajoDto[];
  total: number;
  pagina: number;
  tamano: number;
}

const R = "/api/reporteria";

export async function obtenerCatalogo(): Promise<Catalogo> {
  const { data } = await axiosClient.get<Catalogo>(`${R}/catalogo/tablas`);
  return data;
}

export async function obtenerRelaciones(): Promise<RelacionSugerida[]> {
  const { data } = await axiosClient.get<RelacionSugerida[]>(`${R}/catalogo/relaciones`);
  return data;
}

export async function obtenerValoresColumna(token: string, max = 200): Promise<unknown[]> {
  const { data } = await axiosClient.get<unknown[]>(`${R}/columnas/${token}/valores?max=${max}`);
  return data;
}

export async function previewReporte(params: PreviewParams): Promise<RepResultadoPagina> {
  const { data } = await axiosClient.post<RepResultadoPagina>(`${R}/preview`, params);
  return data;
}

export async function contarReporte(def: RepDefinicion): Promise<number> {
  const { data } = await axiosClient.post<{ total: number }>(`${R}/contar`, def);
  return data.total;
}

export async function encolarExportacion(def: RepDefinicion, titulo: string): Promise<number> {
  const { data } = await axiosClient.post<{ idTrabajo: number }>(`${R}/exportar`, { def, titulo });
  return data.idTrabajo;
}

export async function listarTrabajos(pagina = 1, tamano = 15): Promise<PaginaTrabajos> {
  const { data } = await axiosClient.get<PaginaTrabajos>(`${R}/trabajos?pagina=${pagina}&tamano=${tamano}`);
  return data;
}

export async function descargarTrabajo(t: TrabajoDto): Promise<void> {
  const response = await encolarDescarga(() =>
    axiosClient.get<Blob>(`${R}/trabajos/${t.id}/descargar`, { responseType: "blob" })
  );
  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = t.nombreArchivo ?? `reporte_${t.id}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

// Templates
export async function listarTemplates(): Promise<TemplateDto[]> {
  const { data } = await axiosClient.get<TemplateDto[]>(`${R}/templates`);
  return data;
}
export async function cargarTemplate(id: number): Promise<RepDefinicion> {
  const { data } = await axiosClient.get<RepDefinicion>(`${R}/templates/${id}`);
  return data;
}
export async function guardarTemplate(body: {
  id?: number | null;
  nombre: string;
  descripcion?: string | null;
  def: RepDefinicion;
}): Promise<number> {
  const { data } = await axiosClient.post<{ id: number }>(`${R}/templates`, body);
  return data.id;
}
export async function eliminarTemplate(id: number): Promise<void> {
  await axiosClient.delete(`${R}/templates/${id}`);
}

// Columnas personalizadas
export async function listarColumnasPersonalizadas(): Promise<ColumnaCalcDto[]> {
  const { data } = await axiosClient.get<ColumnaCalcDto[]>(`${R}/columnas-personalizadas`);
  return data;
}
export async function guardarColumnaPersonalizada(body: {
  id?: number | null;
  nombre: string;
  formula: string;
  tipo: string;
  formato?: string | null;
}): Promise<number> {
  const { data } = await axiosClient.post<{ id: number }>(`${R}/columnas-personalizadas`, body);
  return data.id;
}
export async function eliminarColumnaPersonalizada(id: number): Promise<void> {
  await axiosClient.delete(`${R}/columnas-personalizadas/${id}`);
}

// Predefinidos
export async function listarPredefinidos(): Promise<ReporteDefaultDto[]> {
  const { data } = await axiosClient.get<ReporteDefaultDto[]>(`${R}/predefinidos`);
  return data;
}
export async function cargarPredefinido(id: number): Promise<RepDefinicion> {
  const { data } = await axiosClient.get<RepDefinicion>(`${R}/predefinidos/${id}`);
  return data;
}
